import fetchSolarPVBatteryCostSupabase from '../components/charts/SolarPVBatteryCostSupabase';
import fetchSolarPVCapacitySupabase from '../components/charts/SolarPVCapacitySupabase';
import fetchSolarPVCostSupabase from '../components/charts/SolarPVCostSupabase';
import fetchSolarPVInstallersCostSupabase from '../components/charts/SolarPVInstallersCostSupabase';
import fetchSolarPVInstallersSupabase from '../components/charts/SolarPVInstallersSupabase';
import fetchSolarPVProjectCountSupabase from '../components/charts/SolarPVProjectCountSupabase';
import {
  fetchElectricitySeries,
  fetchElectricityStacked,
  fetchNaturalGasSeries,
  fetchNaturalGasStacked,
  type EnergySeriesResult,
} from '../components/charts/EnergyUseSupabase';
import { fetchAllPvSize } from './allPvSizeService';
import {
  fetchAffordableHousingAprCapacity,
  fetchAffordableHousingAprPipeline,
  fetchAffordableHousingAprPreservation,
  fetchAffordableHousingAprSummary,
} from './affordableHousingAprService';
import { fetchBusinessEstablishmentRows } from './businessEstablishmentsService';
import { fetchCarbonIntensityData } from './carbonIntensityService';
import { fetchElectricityPricesByGeography } from './electricityPricesByGeographyService';
import { fetchHeatPumps } from './heatPumpService';
import { fetchSolarTechnicalPotential } from './solarTechnicalPotentialService';
import { supabaseClient } from './supabase';

function slugColumn(label: string): string {
  return label
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

function geofipsVariants(geofips: number): Array<string | number> {
  const raw = String(geofips).trim();
  const unpadded = raw.replace(/^0+/, '') || '0';
  const padded = raw.padStart(7, '0');
  const variants = new Set<string | number>([raw, unpadded, padded]);
  const numeric = Number(unpadded);
  if (!Number.isNaN(numeric)) variants.add(numeric);
  return Array.from(variants);
}

function flattenChartDatasets(
  location: string,
  chartData: { labels: unknown[]; datasets: Array<{ label: string; data: unknown[] }> },
  extra: Record<string, unknown> = {}
): Record<string, unknown>[] {
  const years = chartData.labels;
  return years.map((year, yearIndex) => {
    const row: Record<string, unknown> = { location, year, ...extra };
    let total = 0;
    for (const dataset of chartData.datasets) {
      const value = Number(dataset.data[yearIndex]) || 0;
      row[slugColumn(dataset.label)] = value;
      total += value;
    }
    row.total = total;
    return row;
  });
}

function flattenEnergySeries(location: string, seriesType: string, result: EnergySeriesResult) {
  return flattenChartDatasets(location, result.chartData, { series_type: seriesType });
}

function flattenCategoryChart(
  location: string,
  chartData: { labels: unknown[]; datasets: Array<{ label: string; data: unknown[] }> },
  categoryKey: string
) {
  const dataset = chartData.datasets[0];
  if (!dataset) return [];
  return chartData.labels.map((label, index) => ({
    location,
    [categoryKey]: label,
    value: dataset.data[index] ?? null,
    series: dataset.label,
  }));
}

async function fetchTableRowsForGeofips(
  table: string,
  geofips: number,
  orderColumn = 'year'
): Promise<Record<string, unknown>[]> {
  for (const variant of geofipsVariants(geofips)) {
    const { data, error } = await supabaseClient
      .from(table)
      .select('*')
      .eq('geofips', variant)
      .order(orderColumn, { ascending: true });
    if (!error && data?.length) return data as Record<string, unknown>[];
  }
  return [];
}

async function fetchHousingToEmployeesRows(location: string, geofips: number) {
  const isCaliforniaState =
    geofips === 6 || location.toLowerCase() === 'california' || location.toLowerCase() === 'state of california';

  if (isCaliforniaState) {
    const { data } = await supabaseClient
      .from('housing_to_employees')
      .select('*')
      .eq('geography_type', 'state')
      .eq('geofips', 6)
      .order('year', { ascending: true });
    return (data || []) as Record<string, unknown>[];
  }

  for (const variant of geofipsVariants(geofips)) {
    const { data: placeData } = await supabaseClient
      .from('housing_to_employees')
      .select('*')
      .eq('geography_type', 'place')
      .eq('geofips', variant)
      .order('year', { ascending: true });
    if (placeData?.length) return placeData as Record<string, unknown>[];
  }

  for (const variant of geofipsVariants(geofips)) {
    const { data: countyData } = await supabaseClient
      .from('housing_to_employees')
      .select('*')
      .eq('geography_type', 'county')
      .eq('geofips', variant)
      .order('year', { ascending: true });
    if (countyData?.length) return countyData as Record<string, unknown>[];
  }

  const { data: byName } = await supabaseClient
    .from('housing_to_employees')
    .select('*')
    .eq('location_name', location)
    .order('year', { ascending: true });
  return (byName || []) as Record<string, unknown>[];
}

async function fetchHeatingFuelRows(location: string) {
  const { data, error } = await supabaseClient
    .from('house_heating_fuel')
    .select('*')
    .ilike('location_name', location)
    .order('year', { ascending: true });
  if (!error && data?.length) return data as Record<string, unknown>[];

  const { data: legacy } = await supabaseClient
    .from('house_heating_fuel')
    .select('*')
    .eq('location_name', location)
    .order('year', { ascending: true });
  return (legacy || []) as Record<string, unknown>[];
}

async function fetchEnergyCostBurdenRows(geofips: number) {
  for (const variant of geofipsVariants(geofips)) {
    const { data, error } = await supabaseClient
      .from('lead_energy_cost_burden_all_frameworks')
      .select('*')
      .eq('geofips', variant)
      .order('year', { ascending: true });
    if (!error && data?.length) return data as Record<string, unknown>[];
  }
  return [];
}

export async function loadElectricityResidentialRows(location: string) {
  const result = await fetchElectricitySeries(location, 'residential', 'average', 'average');
  return flattenEnergySeries(location, 'electricity_residential', result);
}

export async function loadElectricityCommercialRows(location: string) {
  const result = await fetchElectricitySeries(location, 'commercial', 'average');
  return flattenEnergySeries(location, 'electricity_commercial', result);
}

export async function loadElectricityTotalRows(location: string) {
  const result = await fetchElectricityStacked(location, 'average', 'average');
  return flattenEnergySeries(location, 'electricity_total', result);
}

export async function loadNaturalGasResidentialRows(location: string) {
  const result = await fetchNaturalGasSeries(location, 'residential', 'average', 'average');
  return flattenEnergySeries(location, 'natural_gas_residential', result);
}

export async function loadNaturalGasCommercialRows(location: string) {
  const result = await fetchNaturalGasSeries(location, 'commercial', 'average');
  return flattenEnergySeries(location, 'natural_gas_commercial', result);
}

export async function loadNaturalGasTotalRows(location: string) {
  const result = await fetchNaturalGasStacked(location, 'average', 'average');
  return flattenEnergySeries(location, 'natural_gas_total', result);
}

export async function loadCarbonIntensityRows(location: string, geofips: number) {
  const result = await fetchCarbonIntensityData(String(geofips));
  return flattenChartDatasets(location, result.chartData, { unit: 'gco2e_per_kwh' });
}

export async function loadElectricityPricesRows(geofips: number) {
  const result = await fetchElectricityPricesByGeography(geofips);
  return [
    ...result.location.map((row) => ({ ...row, scope: 'location' })),
    ...result.california.map((row) => ({ ...row, scope: 'california' })),
  ];
}

export async function loadSolarPvCapacityRows(location: string) {
  const result = await fetchSolarPVCapacitySupabase(location);
  return flattenChartDatasets(location, result.chartData, { unit: 'mw' });
}

export async function loadSolarPvCostRows(location: string) {
  const result = await fetchSolarPVCostSupabase(location);
  return flattenChartDatasets(location, result.chartData, { unit: 'usd_per_watt' });
}

export async function loadSolarPvProjectCountRows(location: string) {
  const result = await fetchSolarPVProjectCountSupabase(location);
  return flattenChartDatasets(location, result.chartData, { unit: 'projects' });
}

export async function loadSolarBatteryCostRows(location: string) {
  const result = await fetchSolarPVBatteryCostSupabase(location);
  return flattenChartDatasets(location, result.chartData, { unit: 'usd_per_kwh' });
}

export async function loadSolarInstallersCapacityRows(location: string) {
  const result = await fetchSolarPVInstallersSupabase(location);
  return flattenCategoryChart(location, result.chartData, 'installer_pair');
}

export async function loadSolarInstallersCostRows(location: string) {
  const result = await fetchSolarPVInstallersCostSupabase(location);
  return flattenCategoryChart(location, result.chartData, 'installer_pair');
}

export async function loadSolarTechnicalPotentialRows(geofips: number | null, location: string) {
  const result = await fetchSolarTechnicalPotential(geofips, location);
  return result.rows.map((row) => ({ location, ...row }));
}

export async function loadHeatPumpRows(geofips: number) {
  const { data } = await fetchHeatPumps({
    select: '*',
    geofips,
    orderByYear: true,
  });
  return (data || []) as Record<string, unknown>[];
}

export async function loadAllPvSizeRows(geofips: number) {
  const { data } = await fetchAllPvSize({ select: '*', geofips });
  return (data || []) as Record<string, unknown>[];
}

export type EnergyBuildingsDownloadConfig = {
  section: string;
  name: string;
  description: string;
  loader: () => Promise<Record<string, unknown>[]>;
};

export function buildEnergyBuildingsDownloadConfigs(
  location: string,
  geofips: number
): EnergyBuildingsDownloadConfig[] {
  return [
    {
      section: 'Energy',
      name: 'Electricity_Residential',
      description: 'Residential electricity consumption by year (kWh)',
      loader: () => loadElectricityResidentialRows(location),
    },
    {
      section: 'Energy',
      name: 'Electricity_Commercial',
      description: 'Commercial electricity consumption by year (kWh)',
      loader: () => loadElectricityCommercialRows(location),
    },
    {
      section: 'Energy',
      name: 'Electricity_Total',
      description: 'Total electricity (residential + commercial) by year',
      loader: () => loadElectricityTotalRows(location),
    },
    {
      section: 'Energy',
      name: 'Natural_Gas_Residential',
      description: 'Residential natural gas consumption by year (therms)',
      loader: () => loadNaturalGasResidentialRows(location),
    },
    {
      section: 'Energy',
      name: 'Natural_Gas_Commercial',
      description: 'Commercial natural gas consumption by year (therms)',
      loader: () => loadNaturalGasCommercialRows(location),
    },
    {
      section: 'Energy',
      name: 'Natural_Gas_Total',
      description: 'Total natural gas (residential + commercial) by year',
      loader: () => loadNaturalGasTotalRows(location),
    },
    {
      section: 'Energy',
      name: 'Carbon_Intensity',
      description: 'Electricity carbon intensity by year (gCO₂e/kWh)',
      loader: () => loadCarbonIntensityRows(location, geofips),
    },
    {
      section: 'Energy',
      name: 'Electricity_Prices',
      description: 'Electricity prices by sector (location + California)',
      loader: () => loadElectricityPricesRows(geofips),
    },
    {
      section: 'Energy',
      name: 'Energy_Cost_Burden',
      description: 'LEAD energy cost burden by framework and income band',
      loader: () => fetchEnergyCostBurdenRows(geofips),
    },
    {
      section: 'Energy',
      name: 'Heating_Fuel',
      description: 'Household heating fuel mix by year',
      loader: () => fetchHeatingFuelRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_PV_Capacity',
      description: 'Solar PV installed capacity by sector (MW)',
      loader: () => loadSolarPvCapacityRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_PV_Cost',
      description: 'Solar PV installed cost by sector ($/W)',
      loader: () => loadSolarPvCostRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_PV_Project_Count',
      description: 'Solar PV project counts by sector',
      loader: () => loadSolarPvProjectCountRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_Battery_Cost',
      description: 'Solar battery installed cost ($/kWh)',
      loader: () => loadSolarBatteryCostRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_Installers_Capacity',
      description: 'Top installer–manufacturer pairs by capacity',
      loader: () => loadSolarInstallersCapacityRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_Installers_Cost',
      description: 'Top installer–manufacturer pairs by cost',
      loader: () => loadSolarInstallersCostRows(location),
    },
    {
      section: 'Energy',
      name: 'Solar_Technical_Potential',
      description: 'Rooftop solar technical and economic potential',
      loader: () => loadSolarTechnicalPotentialRows(geofips, location),
    },
    {
      section: 'Energy',
      name: 'Solar_PV_Size_Raw',
      description: 'Raw all_pv_size records for this location',
      loader: () => loadAllPvSizeRows(geofips),
    },
    {
      section: 'Energy',
      name: 'Heat_Pumps',
      description: 'Heat pump installations by year and system type',
      loader: () => loadHeatPumpRows(geofips),
    },
    {
      section: 'Energy',
      name: 'Business_Establishments',
      description: 'Business establishments by sector and size class',
      loader: async () => {
        const { rows } = await fetchBusinessEstablishmentRows(String(geofips));
        return rows.map((row) => ({ location, ...row }));
      },
    },
    {
      section: 'Energy',
      name: 'Housing_to_Employees',
      description: 'Housing units and employees by year',
      loader: () => fetchHousingToEmployeesRows(location, geofips),
    },
    {
      section: 'Energy',
      name: 'Single_Family_Detached',
      description: 'Single-family detached housing share by year',
      loader: () => fetchTableRowsForGeofips('single_family_detached', geofips),
    },
    {
      section: 'Energy',
      name: 'Market_Rent',
      description: 'Housing units by market rent category and year',
      loader: () => fetchTableRowsForGeofips('market_rent_percent', geofips),
    },
    {
      section: 'Energy',
      name: 'Affordable_Housing_Summary',
      description: 'Affordable housing APR summary by year',
      loader: async () => {
        const rows = await fetchAffordableHousingAprSummary(geofips, location);
        return rows.map((row) => ({ location, ...row }));
      },
    },
    {
      section: 'Energy',
      name: 'Affordable_Housing_Pipeline',
      description: 'Affordable housing pipeline units by stage',
      loader: async () => {
        const rows = await fetchAffordableHousingAprPipeline(geofips, location);
        return rows.map((row) => ({ location, ...row }));
      },
    },
    {
      section: 'Energy',
      name: 'Affordable_Housing_Preservation',
      description: 'Affordable housing preservation activity',
      loader: async () => {
        const rows = await fetchAffordableHousingAprPreservation(geofips, location);
        return rows.map((row) => ({ location, ...row }));
      },
    },
    {
      section: 'Energy',
      name: 'Affordable_Housing_Capacity',
      description: 'Realistic affordable housing capacity by year',
      loader: async () => {
        const rows = await fetchAffordableHousingAprCapacity(geofips, location);
        return rows.map((row) => ({ location, ...row }));
      },
    },
  ];
}
